import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { hasAnyPermission } from '../services/authService';
import './Sidebar.css';

function Sidebar({ isExpanded, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [hoveredMenu, setHoveredMenu] = useState(null);

  // 사용자 정보에서 슈퍼어드민 여부 확인
  const getUserInfo = () => {
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const user = getUserInfo();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  // 권한 체크 함수
  const hasMenuPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return hasAnyPermission(permissions);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const allMenuItems = [
    {
      id: 'enterprise',
      icon: 'apartment',
      title: '전사 모니터링',
      requiredPermissions: ['enterprise.view'],
      submenus: [
        { name: '전사 통합 대시보드', path: '/enterprise', permissions: ['enterprise.view'] },
        { name: '공장별 사출 현황', path: '/enterprise-factory-overview', permissions: ['enterprise.view'] },
      ]
    },
    {
      id: 'factory',
      icon: 'factory',
      title: '공장 모니터링',
      requiredPermissions: ['factory_monitoring.view'],
      submenus: [
        { name: '공장 사출 현황', path: '/factory', permissions: ['factory_monitoring.view'] },
        { name: '사출기 현황', path: '/factory-machine-overview', permissions: ['factory_monitoring.view'] },
        { name: '실시간 샷 현황', path: '/realtime-shot-status', permissions: ['factory_monitoring.view'] }
      ]
    },
    {
      id: 'machine',
      icon: 'conveyor_belt',
      title: '사출기 모니터링',
      requiredPermissions: ['machine.view'],
      submenus: [
        { name: '사출기 대시보드', path: '/machine', permissions: ['machine.view'] },
        { name: '설비 현황판', path: '/equipment-board', permissions: ['machine.view'] },
      ]
    },
    {
      id: 'ai',
      icon: 'text_fields_alt',
      title: 'AI 분석',
      requiredPermissions: ['ai.view'],
      submenus: [
        { name: 'AI 분석', path: '/ai-analysis', permissions: ['ai.view'] },
      ]
    },
    {
      id: 'management',
      icon: 'room_preferences',
      title: '공장/설비관리',
      requiredPermissions: ['factory.view', 'equipment.view'],
      submenus: [
        { name: '공장현황', path: '/factory-list', permissions: ['factory.view'] },
        { name: '공장등록', path: '/factory/register', permissions: ['factory.create'] },
        { name: '설비현황', path: '/equipment-list', permissions: ['equipment.view'] },
        { name: '설비등록', path: '/equipment/register', permissions: ['equipment.create'] }
      ]
    },
    {
      id: 'user',
      icon: 'manage_accounts',
      title: '회원관리',
      requiredPermissions: ['user.view', 'role.view', 'notice.view'],
      submenus: [
        { name: '회원현황', path: '/user', permissions: ['user.view'] },
        { name: '회원등록', path: '/user/register', permissions: ['user.create'] },
        { name: '권한관리', path: '/role-management', permissions: ['role.view'] },
        { name: '공지현황', path: '/notices', permissions: ['notice.view'] }
      ]
    },
    {
      id: 'system',
      icon: 'settings',
      title: '시스템 관리',
      requiredPermissions: ['system.view'],
      submenus: [
        { name: '일반설정', path: '/system?tab=general', permissions: ['system.view'] },
        { name: '모니터링 설정', path: '/system?tab=monitoring', permissions: ['system.view'] },
        { name: '알림설정', path: '/system?tab=alarm', permissions: ['system.view'] },
        { name: '데이터 수집관리', path: '/system?tab=data-collection', permissions: ['system.view'] },
        { name: '시스템로그', path: '/system?tab=log', permissions: ['system.view'] },
        { name: 'IP관리', path: '/system/ip', permissions: ['system.view'] }
      ]
    },
    {
      id: 'platform',
      icon: 'admin_panel_settings',
      title: '플랫폼 관리',
      requiredPermissions: ['platform.view'],
      superAdminOnly: true,
      submenus: [
        { name: '회사 관리', path: '/platform/companies', permissions: ['platform.view'] },
        { name: '전체 사용자', path: '/platform/users', permissions: ['platform.view'] },
        { name: '시스템 현황', path: '/platform/status', permissions: ['platform.view'] },
        { name: '라이선스 관리', path: '/platform/licenses', permissions: ['platform.view'] }
      ]
    }
  ];

  // 권한에 따라 메뉴 필터링
  const menuItems = allMenuItems.filter(menu => {
    // 슈퍼어드민 전용 메뉴 체크
    if (menu.superAdminOnly && !isSuperAdmin) {
      return false;
    }
    // 권한 체크
    return hasMenuPermission(menu.requiredPermissions);
  }).map(menu => ({
    ...menu,
    // 서브메뉴도 권한에 따라 필터링
    submenus: menu.submenus.filter(submenu =>
      hasMenuPermission(submenu.permissions)
    )
  })).filter(menu => menu.submenus.length > 0); // 서브메뉴가 없으면 메뉴 자체를 숨김

  const toggleSubmenu = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId);
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  // 현재 페이지가 해당 메뉴에 속하는지 확인
  const isActiveMenu = (item) => {
    return item.submenus.some(submenu => {
      const submenuPath = submenu.path.split('?')[0]; // 쿼리 파라미터 제거
      const currentPath = location.pathname;
      return currentPath === submenuPath;
    });
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  return (
    <>
      {/* 기본 아이콘 사이드바 */}
      <aside className="sidebar">
        <div className="sidebar-icons">
          {menuItems.map((item) => (
            <div
              key={item.id}
              className="sidebar-icon"
              title={item.title}
              onClick={() => item.submenus.length > 0 && navigate(item.submenus[0].path)}
              onMouseEnter={() => setHoveredMenu(item.id)}
              onMouseLeave={() => setHoveredMenu(null)}
            >
              <span
                className="material-symbols-outlined"
                style={{
                  color: isActiveMenu(item) ? '#3B82F6' : '#CAD2E0',
                  fontSize: '24px'
                }}
              >
                {item.icon}
              </span>

              {/* Hover 시 나타나는 서브메뉴 툴팁 */}
              {hoveredMenu === item.id && (
                <div className="sidebar-tooltip">
                  <div className="sidebar-tooltip-title">{item.title}</div>
                  <div className="sidebar-tooltip-menu">
                    {item.submenus.map((submenu, index) => (
                      <div
                        key={index}
                        className="sidebar-tooltip-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(submenu.path);
                        }}
                      >
                        {submenu.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

        </div>
      </aside>

      {/* 확장형 사이드바 */}
      <aside className={`sidebar-expanded ${isExpanded ? 'open' : ''}`}>
        

        <div className="sidebar-datetime">
          <div className="sidebar-date">{formatDate(currentTime)}</div>
          <div className="sidebar-time">{formatTime(currentTime)}</div>
        </div>

        <nav className="sidebar-menu">
          {menuItems.map((item) => (
            <div key={item.id} className="sidebar-menu-item">
              <div
                className={`sidebar-menu-header ${expandedMenu === item.id ? 'active' : ''}`}
                onClick={() => toggleSubmenu(item.id)}
              >
                <div className="sidebar-menu-title">
                  <span className="material-symbols-outlined sidebar-menu-icon">
                    {item.icon}
                  </span>
                  <span>{item.title}</span>
                </div>
                <span className={`material-symbols-outlined sidebar-menu-arrow ${expandedMenu === item.id ? 'rotate' : ''}`}>
                  expand_more
                </span>
              </div>

              {expandedMenu === item.id && (
                <div className="sidebar-submenu">
                  {item.submenus.map((submenu, index) => (
                    <div
                      key={index}
                      className="sidebar-submenu-item"
                      onClick={() => handleNavigation(submenu.path)}
                    >
                      {submenu.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

      </aside>
    </>
  );
}

export default Sidebar;
